/**
 * Log levels in order of severity (lowest to highest verbosity)
 */
export type LogLevel = "error" | "warn" | "info" | "log" | "debug" | "trace"

/**
 * Numeric values for log levels, used for comparison
 */
export const LOG_LEVELS: Record<LogLevel, number> = {
    error: 0,
    warn: 1,
    info: 2,
    log: 3,
    debug: 4,
    trace: 5,
}

/**
 * Method signature for logging functions
 */
export type LogMethod = (message: string, ...additionalData: unknown[]) => void

/**
 * Method signature for lazy logging functions that only evaluate when logging is enabled
 */
export type LazyLogMethod = (messageFn: () => string, ...additionalData: unknown[]) => void

/**
 * Platform-agnostic logger interface
 */
export type CmLogger = {
    error: LogMethod
    warn: LogMethod
    info: LogMethod
    log: LogMethod
    debug: LogMethod
    trace: LogMethod

    /**
     * Check if logging is enabled at the specified level.
     * Use this to avoid expensive computations when logging is disabled.
     */
    isEnabled: (level: LogLevel) => boolean

    /**
     * Lazy logging methods that only evaluate the message function when logging is enabled.
     * Use these to avoid expensive string formatting or computations when logging is disabled.
     */
    errorLazy: LazyLogMethod
    warnLazy: LazyLogMethod
    infoLazy: LazyLogMethod
    logLazy: LazyLogMethod
    debugLazy: LazyLogMethod
    traceLazy: LazyLogMethod
}

/**
 * A logger that does nothing - useful as a default/fallback
 */
export const NoopLogger: CmLogger = {
    error: () => {},
    warn: () => {},
    info: () => {},
    log: () => {},
    debug: () => {},
    trace: () => {},
    isEnabled: () => false,
    errorLazy: () => {},
    warnLazy: () => {},
    infoLazy: () => {},
    logLazy: () => {},
    debugLazy: () => {},
    traceLazy: () => {},
}

/**
 * Minimal interface for a console-like logger (subset of console or winston Logger)
 */
export type MinimalLogger = {
    error: (message: string, ...args: unknown[]) => void
    warn: (message: string, ...args: unknown[]) => void
    info: (message: string, ...args: unknown[]) => void
    log?: (message: string, ...args: unknown[]) => void
    debug: (message: string, ...args: unknown[]) => void
    trace?: (message: string, ...args: unknown[]) => void
}

/**
 * Wrap a minimal console-like logger to implement the full CmLogger interface.
 * Useful for adapting `console` or other loggers.
 *
 * @param logger - The underlying logger (e.g., console)
 * @param level - Optional log level (defaults to "debug", meaning all messages are logged)
 */
export function wrapLogger(logger: MinimalLogger, level: LogLevel = "debug"): CmLogger {
    const isEnabled = (checkLevel: LogLevel): boolean => {
        return LOG_LEVELS[checkLevel] <= LOG_LEVELS[level]
    }

    return {
        error: (message: string, ...data: unknown[]) => logger.error(message, ...data),
        warn: (message: string, ...data: unknown[]) => logger.warn(message, ...data),
        info: (message: string, ...data: unknown[]) => logger.info(message, ...data),
        log: (message: string, ...data: unknown[]) => (logger.log ?? logger.info)(message, ...data),
        debug: (message: string, ...data: unknown[]) => logger.debug(message, ...data),
        trace: (message: string, ...data: unknown[]) => (logger.trace ?? logger.debug)(message, ...data),
        isEnabled,
        errorLazy: (messageFn: () => string, ...data: unknown[]) => {
            if (isEnabled("error")) logger.error(messageFn(), ...data)
        },
        warnLazy: (messageFn: () => string, ...data: unknown[]) => {
            if (isEnabled("warn")) logger.warn(messageFn(), ...data)
        },
        infoLazy: (messageFn: () => string, ...data: unknown[]) => {
            if (isEnabled("info")) logger.info(messageFn(), ...data)
        },
        logLazy: (messageFn: () => string, ...data: unknown[]) => {
            if (isEnabled("log")) (logger.log ?? logger.info)(messageFn(), ...data)
        },
        debugLazy: (messageFn: () => string, ...data: unknown[]) => {
            if (isEnabled("debug")) logger.debug(messageFn(), ...data)
        },
        traceLazy: (messageFn: () => string, ...data: unknown[]) => {
            if (isEnabled("trace")) (logger.trace ?? logger.debug)(messageFn(), ...data)
        },
    }
}

/**
 * Named domains for loggers, allowing domain-specific log level configuration
 */
export type LoggerDomain =
    | "api"
    | "app"
    | "ar"
    | "auth"
    | "image-processing"
    | "legacy"
    | "routing"
    | "rendering"
    | "workers"
    | "webassembly"
    | "scanning"
    | "scene"
    | "templates"
    | "materials"
    | "pictures"
    | "textures"
    | "graph"
    | "config"
    | "upload"
    | "jobs"
    | "webgl2"
    | "image-op-system"

/**
 * All available logger domains
 */
export const LOGGER_DOMAINS: LoggerDomain[] = [
    "api",
    "app",
    "ar",
    "auth",
    "image-processing",
    "legacy",
    "routing",
    "rendering",
    "workers",
    "webassembly",
    "scanning",
    "scene",
    "templates",
    "materials",
    "pictures",
    "textures",
    "graph",
    "config",
    "upload",
    "jobs",
    "webgl2",
    "image-op-system",
]
